<template>
  <v-row align="center">
    <!-- mb-3 -->
    <v-expansion-panels>
      <v-expansion-panel>
        <v-expansion-panel-title>
          <span>{{ name || title }}</span>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row align="center" style="padding: 0px 20px 0 20px; position: relative">
            <v-btn v-if="readonly ? false : index && index > 0" class="delete-btn" icon @click.native="removeEmailTemplate()">
              <v-icon color="grey-lighten-1">mdi-delete-outline</v-icon>
            </v-btn>
            <v-col cols="12" sm="3" v-if="allowNameChange">
              <v-text-field :readonly="readonly" v-model="name" label="Name" max-length="50"></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <v-switch :readonly="readonly" color="black" v-model="hasHighImportanceFlag" label="High Importance"></v-switch>
            </v-col>
            <v-col cols="12" sm="3">
              <v-switch
                :readonly="readonly"
                color="black"
                v-model="isExternal"
                :label="isExternal ? 'Email will only be sent to external users' : 'Email will only be sent to internal users'"
              ></v-switch>
            </v-col>
            <v-col cols="12" sm="3">
              <v-switch :readonly="readonly" color="black" v-model="enabled" :label="enabled ? 'Enabled' : 'Disabled'"></v-switch>
            </v-col>
            <v-col cols="12" sm="4" v-if="allowDaysIntervalConf && !customFrequency">
              <v-text-field
                type="number"
                label="Days Interval"
                v-model="daysInterval"
                min="0"
                :readonly="readonly"
                :rules="validateDaysInterval"
                required
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="4" v-if="allowSkip && !customFrequency">
              <v-text-field
                :readonly="readonly"
                type="number"
                label="Skips before sending first one"
                v-model="skips"
                min="0"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row align="center" style="padding: 0px 20px 0 20px">
            <!-- mb-3 -->
            <v-col cols="12" sm="4">
              <!-- mb-2 -->
              <label>To</label>
              <v-select
                v-model="selectedTos"
                :items="roles"
                item-title="name"
                item-value="code"
                :error-messages="validateNotEmptyTo()"
                :menu-props="{ maxHeight: '400' }"
                label="Select"
                multiple
                :readonly="readonly"
                hint="Pick roles to send notifications"
                persistent-hint
              ></v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <!-- mb-2 -->
              <label>CC</label>
              <v-select
                v-model="selectedCcs"
                :items="roles"
                item-title="name"
                item-value="code"
                :readonly="readonly"
                :menu-props="{ maxHeight: '400' }"
                label="Select"
                multiple
                hint="Pick roles to send notifications as CC"
                persistent-hint
              ></v-select>
            </v-col>
            <v-col cols="12" sm="4">
              <label>BCC</label>
              <v-select
                v-model="selectedBccs"
                :items="roles"
                item-title="name"
                item-value="code"
                :menu-props="{ maxHeight: '400' }"
                label="Select"
                :readonly="readonly"
                multiple
                hint="Pick roles to send notifications as BCC"
                persistent-hint
              ></v-select>
            </v-col>
            <v-col cols="12" sm="12" v-if="!readonly">
              <DraggableChip
                v-once
                class="mt-1 mr-1"
                :key="key"
                :readonly="readonly"
                @onClick="onChipClick(key)"
                v-for="key in extractPath(dummy)"
                :value="getChipText(key)"
                :label="getValueFromPath(dummy, key, 'Not found')"
              />
            </v-col>
            <v-col cols="12" sm="12">
              <v-text-field
                :readonly="readonly"
                :error-messages="validateNotEmptySubject()"
                label="Subject"
                v-model="subject"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="12">
              <label>Content</label>
              <div :class="['email-template', { 'error-border': isContentEmpty() }]">
                <quill-editor ref="editor" v-model:value="content" :options="editorOptions" v-if="!readonly"></quill-editor>
                <div v-else v-html="content" class="pa-2 readonly-content"></div>
              </div>
              <label v-if="isContentEmpty()" class="custom-error-text">Content is required</label>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-row>
</template>

<script>
import { quillEditor, Quill } from 'vue3-quill'
import DraggableChip from './DraggableChip.vue'
import { emailBodyAndSubjectByStatusDefault, Roles, NOTIFICATION_TAGS, NOTIFICATION_STATUS } from '@/common/notifications'

let dummyMOC = {
  title: 'MOC Title',
  mocId: 'MOC Id',
  url: 'MOC URL',
  community: 'Community Name',
  changeCategory: 'Change Category',
  mocType: 'MOC Type',
  status: 'Status',
  description: 'MOC Description',
  justification: 'MOC Justification',
  commentsFacilitator: 'Comments for Facilitator',
  approvalProcessType: {
    name: 'Approval Process Type Name'
  },
  changeType: {
    type: 'Change Type type'
  },
  approvers: 'Approvers',
  fyis: 'FYIs',
  coinitiator: 'Co-Initiator',
  facilitator: 'Facilitator',
  facilitatorBackup: 'Facilitator Backup',
  coordinator: 'Coordinator',
  owner: 'Owner',
  initiator: 'Initiator',
  ownerDecision: 'Owner Decision',
  ownerDecisionReason: 'Owner Decision Reason',
  approveBy: 'Date of decision end',
  sentBackReason: {
    reason: 'Sent Back Reason',
    sentBackBy: 'Sent Back By',
    sentBackDate: 'Sent Back Date'
  },
  closedVoidReason: {
    reason: 'Closed Void Reason',
    closedVoidBy: 'Closed Void By',
    closedVoidDate: 'Closed Void Date'
  },
  closedStrandedReason: {
    justification: 'Closed Stranded Justification',
    closedStrandedBy: 'Closed Stranded By',
    closedStrandedDate: 'Closed Stranded Date'
  },
  revisionReason: {
    date: 'Revision Reason Date',
    reason: 'Revision Reason',
    user: 'Revision Reason User'
  },
  dates: {
    initiated: 'Date Initiated',
    start: 'Start Date Proposed',
    submitted: 'Date Submitted',
    validUntil: 'Valid Until Date',
    availableForDecision: 'Available For Decision Date',
    approvedStart: 'Approved Start Date'
  },
  closerDeniedReason: {
    reason: 'Closer denied reason'
  },
  approversMissingDecision: 'Approvers that have not made a decision',
  approvedCount: 'Approvals Count',
  rejectedCount: 'Rejections Count',
  noVoteCount: 'No Decision Count',
  totalApproversCount: 'Total Approvers Count'
}

let arraysInDummy = {
  fyis: true,
  approvers: true,
  additionalApprovers: true,
  additionalFyis: true,
  approversMissingDecision: true
}

let memoized = {}

const getDummy = defaultEmail => {
  let dummy = { ...dummyMOC }
  if (defaultEmail && defaultEmail.extraProps) {
    defaultEmail.extraProps.forEach(prop => {
      Object.keys(prop).forEach(k => {
        dummy[k] = prop[k].label
        if (prop[k].isArray) {
          arraysInDummy[k] = true
        }
      })
    })

    return dummy
  }

  return dummyMOC
}

export default {
  name: 'EmailTemplate',
  components: {
    quillEditor,
    DraggableChip
  },
  props: {
    emailType: String,
    title: String,
    index: Number,
    notificationEmail: Object,
    allowDaysIntervalConf: Boolean,
    allowNameChange: Boolean,
    allowSkip: Boolean,
    notificationsEnabled: Boolean,
    readonly: Boolean
  },
  data() {
    const content = emailBodyAndSubjectByStatusDefault[this.emailType].content
    const subject = emailBodyAndSubjectByStatusDefault[this.emailType].subject
    const defaultTagEmail = NOTIFICATION_TAGS.find(k => k.key === this.notificationEmail.tag)
    const defaultStatusMail = NOTIFICATION_STATUS.find(k => k.value === this.notificationEmail.forStatus)
    const defaultMail = defaultTagEmail || defaultStatusMail
    return {
      roles: defaultMail && defaultMail.extraRoles ? Roles.concat(defaultMail.extraRoles) : Roles,
      editorOptions: {
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            ['blockquote', 'code-block'],
            [{ header: 1 }, { header: 2 }],
            [{ list: 'ordered' }, { list: 'bullet' }],
            [{ script: 'sub' }, { script: 'super' }],
            [{ indent: '-1' }, { indent: '+1' }],
            [{ direction: 'rtl' }],
            [{ size: ['small', false, 'large', 'huge'] }],
            [{ header: [1, 2, 3, 4, 5, 6, false] }],
            [{ font: [] }],
            [{ color: [] }, { background: [] }],
            [{ align: [] }],
            ['clean'],
            ['link', 'image']
          ]
        }
      },
      readonlyEditorOptions: {
        modules: {}
      },
      content,
      selectedTos: [],
      selectedBccs: [],
      selectedCcs: [],
      hasHighImportanceFlag: false,
      subject,
      daysInterval: 5,
      skips: 0,
      dummy: getDummy(defaultMail),
      name: '',
      enabled: true,
      isExternal: false,
      customFrequency: defaultMail.customFrequency
    }
  },
  created() {
    if (this.notificationEmail.id || this.notificationEmail.dirty) {
      const { template, to, cc, bcc, subject, daysInterval, name, enabled, skips, isExternal } = this.notificationEmail
      this.content = template === undefined || template === null ? this.content : template
      this.selectedTos = to || []
      this.selectedCcs = cc || []
      this.selectedBccs = bcc || []
      this.subject = subject === undefined || subject === null ? this.subject : subject
      this.daysInterval = Number(daysInterval)
      this.name = name
      this.enabled = enabled
      this.skips = skips
      this.isExternal = isExternal
    } else {
      this.handleUpdate()
    }
  },
  // updated() {
  //   //updated hook is not working, use watch instead
  //   this.handleUpdate()
  // },
  computed: {
    validateDaysInterval() {
      return [() => (this.daysInterval < 0 ? 'This field cannot be less than zero' : true)]
    }
  },
  methods: {
    handleUpdate() {
      const {
        content,
        selectedTos,
        selectedBccs,
        selectedCcs,
        subject,
        hasHighImportanceFlag,
        daysInterval,
        skips,
        allowDaysIntervalConf,
        notificationEmail,
        emailType,
        name,
        enabled,
        isExternal
      } = this
      this.$emit(
        'emailTemplateFormChanged',
        {
          template: content,
          to: selectedTos,
          bcc: selectedBccs,
          cc: selectedCcs,
          emailType,
          subject,
          hasHighImportanceFlag,
          daysInterval: allowDaysIntervalConf ? Number(daysInterval) : 0,
          forStatus: notificationEmail.forStatus,
          name,
          skips,
          enabled:enabled,
          dirty: true,
          tag: notificationEmail.tag,
          isExternal
        },
        this.notificationEmail.clientID || this.notificationEmail.id
      )
    },
    validateNotEmptyTo() {
      return !this.enabled || !this.notificationsEnabled
        ? null
        : this.selectedTos && this.selectedTos.length > 0
        ? null
        : 'To is required'
    },
    isContentEmpty() {
      if (!this.enabled || !this.notificationsEnabled) return false
      const parser = new DOMParser()
      const { textContent } = parser.parseFromString(this.content, 'text/html').documentElement
      return !textContent.trim()
    },
    validateNotEmptySubject() {
      if (!this.enabled) return ''
      if (this.subject && this.subject.trim()) return ''
      return 'Subject is required'
    },
    getChipText(key) {
      if (!arraysInDummy[key]) {
        return `{{${key}}}`
      }
      return `\n{{#${key}}}{{/${key}}}`
    },
    onChipClick(key) {
      this.$refs.editor.$el.focus()
      const range = this.$refs.editor.editor.__quill.getSelection(true)
      this.$refs.editor.editor.__quill.insertText(range, this.getChipText(key))
    },
    removeEmailTemplate() {
      this.$emit('onDelete', this.notificationEmail.clientID, this.emailType)
    },
    extractPath(object, prefixes) {
      let paths = []
      let value
      let key

      prefixes = prefixes || []

      if (typeof object === 'object') {
        for (key in object) {
          value = object[key]

          if (typeof value === 'object') {
            paths = paths.concat(this.extractPath(value, prefixes.concat([key])))
          } else {
            paths.push(prefixes.concat(key).join(';'))
          }
        }
      }

      return paths
    },
    getValueFromPath(object, path, defaultVal) {
      if (object === undefined || object === null || path === undefined || path === null) {
        return defaultVal
      }

      let names

      if (typeof path === 'string') {
        if (memoized[path]) {
          names = memoized[path].slice(0)
        } else {
          names = path.split(';').reverse()
          memoized[path] = names.slice(0)
        }
      }

      while (names.length && (object = object[names.pop()]) !== undefined && object !== null);

      // Handle cases where accessing a childprop of a null value
      if (object === null && names.length) object = undefined

      return object === undefined ? defaultVal : object
    }
  },
  watch: {
    name: function() { this.handleUpdate() },
    hasHighImportanceFlag: function() { this.handleUpdate() },
    isExternal: function() { this.handleUpdate() },
    enabled: function() { this.handleUpdate() },
    daysInterval: function() { this.handleUpdate() },
    skips: function() { this.handleUpdate() },
    selectedTos: function() { console.log(`email watch selectedTos`); this.handleUpdate() },
    selectedCcs: function() { this.handleUpdate() },
    selectedBccs: function() { this.handleUpdate() },
    subject: function() { this.handleUpdate() },
    content: function() { this.handleUpdate() }
  }
}
</script>

<style>
.ql-editor {
  max-height: 300px;
}

.theme--dark .ql-snow .ql-fill {
  fill: #ffffff;
}

.theme--dark .ql-snow .ql-stroke {
  stroke: #ffffff;
}

.theme--dark .ql-snow .ql-picker {
  color: #ffffff;
}
</style>
<style scoped>
.custom-error-text {
  color: #d50000 !important;
  font-size: 12px;
}

.error-border {
  border: 2px #d50000 solid;
}

.delete-btn {
  position: absolute;
  right: 2%;
  z-index: 20;
}

.readonly-content {
  border: 1px #ccc solid;
  font-size: 12px;
  max-height: 300px;
  min-height: 300px;
  overflow-y: scroll;
  overflow-x: hidden;
}
</style>
